import * as React from "react";
import { Marker, InfoWindow } from "@react-google-maps/api";
import { Copy, images, Routes } from "utils";
import { ORDER_TYPES } from "utils/constants";
import { Condition } from "components/elements";
import { Text, Button } from "components/elementsThemed";
import { useCell } from "hooks";
import css from "./locationsMap.module.scss";

const { PICKUP, DELIVERY, KIOSK } = ORDER_TYPES;

const {
  PREORDER_BUTTON_TEXT,
  STORE_CLOSED,
  ORDER_BUTTON_TEXT_DELIVERY,
  ORDER_BUTTON_TEXT_KIOSK,
  ORDER_BUTTON_TEXT_PICKUP,
} = Copy.LOCATION_LIST_STATIC;

const orderTypeButtonCopyMap = {
  [DELIVERY]: ORDER_BUTTON_TEXT_DELIVERY,
  [KIOSK]: ORDER_BUTTON_TEXT_KIOSK,
  [PICKUP]: ORDER_BUTTON_TEXT_PICKUP,
};

const orderTypeMap = {
  delivery: "delivery",
  kiosk: "dine-in",
  pickup: "pickup",
};

const { Condition: If } = Condition;

const locationMarkerImage = images?.indicator_pin_location;

const LocationMarker = ({
  type,
  isSelectedLocation,
  onClick,
  onClose,
  id,
  name,
  isDeliveryEnabled,
  isPickupEnabled,
  address,
  hours,
  slug,
  restaurantGroup,
  isOpen,
  diningOption,
  history,
  isPreorderRequired,
  isMarketPlace,
}) => {
  const { labelTextStyles: labels, buttons } = useCell(type);

  // location handle
  const locationHandle = slug?.toLowerCase();

  // RG menu Route
  const locationRoute = `${Routes.LOCATION_INFO({
    orderType: orderTypeMap[diningOption],
    resGroupId: !isMarketPlace ? restaurantGroup : id,
    slug: locationHandle,
  })}`;

  const addModalParamIfNecessary = () =>
    isPreorderRequired ? "?isLocationModalOpen=true" : "";

  // button text
  const getOrderButtonCopy = () => {
    if (!isOpen) return STORE_CLOSED;
    if (isPreorderRequired) return PREORDER_BUTTON_TEXT;
    return orderTypeButtonCopyMap[diningOption];
  };

  return (
    <Marker
      key={id}
      icon={{
        scaledSize: new window.google.maps.Size(50, 50),
        url: locationMarkerImage,
      }}
      title={name}
      position={{ lat: address.geo.lat, lng: address.geo.long }}
      onClick={onClick}
    >
      <If is={isSelectedLocation}>
        <InfoWindow
          onCloseClick={onClose}
          position={{ lat: address.geo.lat, lng: address.geo.long }}
        >
          <div className={css["locationsMap-info"]}>
            <div className={css["locationsMap-info--name"]}>
              <Text type={labels.primary} block>
                {name}
              </Text>
            </div>
            <If is={!isDeliveryEnabled}>
              <Text type={labels.primary}>
                {Copy.LOCATIONS_STATIC.NO_DELIVERY_TEXT}
              </Text>
            </If>
            <If is={!isPickupEnabled}>
              <Text type={labels.primary}>
                {Copy.LOCATIONS_STATIC.NO_PICKUP_TEXT}
              </Text>
            </If>
            <div className={css["locationsMap-info--address"]}>
              <Text type={labels.secondary} className={css.title} block>
                {Copy.LOCATIONS_STATIC.ADDRESS_HEADER_TEXT}
              </Text>
              <Text type={labels.tertiary} block>
                {address.street1} {address?.street2}
              </Text>
              <div>
                <Text type={labels.tertiary}>
                  {`${address.city} ${address.state} ${address.zip}`}
                </Text>
              </div>
            </div>
            <If is={hours.todayDelivery && isDeliveryEnabled}>
              <div className={css["locationsMap-info--hour"]}>
                <Text type={labels.secondary} block>
                  {Copy.LOCATIONS_STATIC.TODAYS_DELIVERY_HOURS}
                </Text>
                <Text
                  type={labels.tertiary}
                  block
                >{`${hours.todayDelivery.text}: ${hours.todayDelivery.hours}`}</Text>
              </div>
            </If>
            <If is={hours.todayPickup && isPickupEnabled}>
              <div className={css["locationsMap-info--hour"]}>
                <Text type={labels.secondary} block>
                  {Copy.LOCATIONS_STATIC.TODAYS_PICK_UP_HOURS}
                </Text>
                <Text
                  type={labels.tertiary}
                  block
                >{`${hours.todayPickup.text}: ${hours.todayPickup.hours}`}</Text>
              </div>
            </If>
            <If is={hours.todayDineIn}>
              <div className={css["locationsMap-info--hour"]}>
                <Text type={labels.secondary} block>
                  {Copy.LOCATIONS_STATIC.TODAYS_STORE_HOURS}
                </Text>
                <Text
                  type={labels.tertiary}
                  block
                >{`${hours.todayDineIn.text}: ${hours.todayDineIn.hours}`}</Text>
              </div>
            </If>
            {diningOption !== DELIVERY && (
              <Button
                type={buttons?.primary}
                onClick={() =>
                  history.push(`${locationRoute}${addModalParamIfNecessary()}`)
                }
                className={`${!isOpen ? css["locationsMap-info--closed"] : ""}`}
              >
                {getOrderButtonCopy()}
              </Button>
            )}
          </div>
        </InfoWindow>
      </If>
    </Marker>
  );
};

export default LocationMarker;
